import { SCORING } from '@/config/audio';
import { requireChord } from '@/data/chords';
import { ChordEvaluator } from '@/audio/chord/evaluator';
import { buildTarget } from '@/audio/chord/target';
import { clarityScore } from '@/audio/strings/stringAnalysis';
import type { AudioEngine, EngineFrame } from '@/audio/engine/AudioEngine';
import type { ChordVerdict, StringReport } from '@/types/audio';
import type { ChordAttempt, SessionSummary } from '@/types/training';
import { chordFeedback, stringAdvice, stringFeedback, type FeedbackItem } from '../feedback';
import { Observable } from '../observable';
import { summarize } from '../summary';

export interface CleanChordSnapshot {
  chordId: string;
  /** Index inside the practice list. */
  position: number;
  total: number;
  listening: boolean;
  /** True once a stable reading has been captured for the current chord. */
  captured: boolean;
  clarity: number;
  verdict: ChordVerdict | null;
  confidence: number;
  strings: StringReport[];
  issues: FeedbackItem[];
  advice: FeedbackItem[];
  /** True when several strings could not be separated — shown as a caveat. */
  hasIndeterminate: boolean;
  /** True while the gate keeps hearing something that is not a guitar. */
  hearingOther: boolean;
  finished: boolean;
  summary: SessionSummary | null;
}

/** Frames of sustained sound required before a reading is accepted. */
const HOLD_FRAMES = 4;

/** Consecutive non-guitar frames before the "I hear other sounds" hint shows. */
const HEARING_OTHER_FRAMES = 8;

/**
 * Clean Chords: play one chord, hold it, get a clarity reading.
 *
 * Unlike the chord-change trainer there is no metronome — the player holds the
 * chord and the engine waits for the sound to settle. A reading is only taken
 * once several consecutive frames agree, so a single spectral accident cannot
 * produce a verdict about a string.
 */
export class CleanChordEngine {
  readonly store: Observable<CleanChordSnapshot>;

  private evaluator: ChordEvaluator;
  private unsubscribeFrame: (() => void) | null = null;
  private holdFrames = 0;
  private cursor = 0;
  private readonly attempts: ChordAttempt[] = [];
  private readonly clarities: number[] = [];
  private startedAt = Date.now();
  private finishing = false;
  private nonGuitarStreak = 0;

  constructor(
    private readonly audio: AudioEngine,
    private readonly chordIds: string[],
    private readonly onFinished?: (summary: SessionSummary) => void,
  ) {
    if (chordIds.length === 0) throw new Error('CleanChordEngine needs at least one chord');
    this.evaluator = new ChordEvaluator(buildTarget(requireChord(chordIds[0])));
    this.store = new Observable<CleanChordSnapshot>({
      chordId: chordIds[0],
      position: 0,
      total: chordIds.length,
      listening: true,
      captured: false,
      clarity: 0,
      verdict: null,
      confidence: 0,
      strings: [],
      issues: [],
      advice: [],
      hasIndeterminate: false,
      hearingOther: false,
      finished: false,
      summary: null,
    });
  }

  get snapshot(): CleanChordSnapshot {
    return this.store.getSnapshot();
  }

  start(): void {
    this.startedAt = Date.now();
    this.audio.setStringAnalysis(true);
    this.audio.setTarget(this.chordIds[0]);
    this.unsubscribeFrame = this.audio.onFrame(this.handleFrame);
  }

  /** Discard the current reading and listen again for the same chord. */
  retry(): void {
    this.holdFrames = 0;
    this.evaluator.reset();
    this.audio.resetAnalysis();
    this.store.update({
      captured: false,
      listening: true,
      clarity: 0,
      verdict: null,
      strings: [],
      issues: [],
      advice: [],
    });
  }

  next(): void {
    if (this.cursor + 1 >= this.chordIds.length) {
      this.finish();
      return;
    }
    this.cursor++;
    const chordId = this.chordIds[this.cursor];
    this.evaluator.setTarget(buildTarget(requireChord(chordId)));
    this.audio.setTarget(chordId);
    this.holdFrames = 0;
    this.store.update({
      chordId,
      position: this.cursor,
      captured: false,
      listening: true,
      clarity: 0,
      verdict: null,
      strings: [],
      issues: [],
      advice: [],
      hasIndeterminate: false,
    });
  }

  /** True once at least one reading was captured — a session worth saving. */
  get hasResults(): boolean {
    return this.attempts.length > 0;
  }

  finish(): void {
    if (this.finishing) return;
    this.finishing = true;
    this.dispose();
    const clarity =
      this.clarities.length > 0
        ? Math.round(this.clarities.reduce((a, b) => a + b, 0) / this.clarities.length)
        : null;
    const summary = summarize({
      mode: 'cleanChord',
      startedAt: this.startedAt,
      durationSec: Math.round((Date.now() - this.startedAt) / 1000),
      chordIds: this.chordIds,
      bpmStart: 0,
      bpmEnd: 0,
      attempts: this.attempts,
      transitions: [],
      rhythmScore: null,
      clarityScore: clarity,
    });
    this.store.update({ finished: true, listening: false, summary });
    this.onFinished?.(summary);
  }

  dispose(): void {
    this.unsubscribeFrame?.();
    this.unsubscribeFrame = null;
    this.audio.setStringAnalysis(false);
    this.audio.setTarget(null);
  }

  private handleFrame = (frame: EngineFrame): void => {
    const snapshot = this.snapshot;
    if (snapshot.captured || snapshot.finished) return;

    // Speech or rustling must not become "string 2 sounds weak" — and neither
    // may an `uncertain` frame: the evaluator's own confidence model knows
    // nothing about the gate and finds loud speech confident-looking. Only
    // frames the gate calls guitar are read; `non-guitar` runs additionally
    // surface a gentle hint. (Sourceless frames come only from unit fixtures.)
    if (frame.source && frame.source.kind !== 'guitar') {
      if (frame.source.kind === 'non-guitar') {
        this.nonGuitarStreak++;
        if (this.nonGuitarStreak === HEARING_OTHER_FRAMES) {
          this.store.update({ hearingOther: true });
        }
      }
      return;
    }
    if (this.nonGuitarStreak > 0) {
      this.nonGuitarStreak = 0;
      if (snapshot.hearingOther) this.store.update({ hearingOther: false });
    }

    const result = this.evaluator.push(frame, frame.bass);

    // Nothing worth reading yet — the player has not struck the chord, or the
    // frame is too quiet or too noisy to say anything about.
    if (result.confidence < SCORING.uncertainConfidence) {
      this.holdFrames = 0;
      this.store.update({ listening: true, confidence: result.confidence });
      return;
    }

    this.holdFrames++;
    if (this.holdFrames < HOLD_FRAMES) {
      this.store.update({ listening: true, confidence: result.confidence });
      return;
    }

    const strings = frame.strings ?? [];
    const clarity = strings.length > 0 ? clarityScore(strings) : Math.round(result.targetScore * 100);
    const issues = strings.length > 0 ? stringFeedback(strings) : chordFeedback(result, requireChord(snapshot.chordId).name);

    this.clarities.push(clarity);
    this.attempts.push({
      chordId: snapshot.chordId,
      verdict: result.status,
      score: result.targetScore,
      confidence: result.confidence,
      bpm: 0,
      transitionSec: null,
    });

    this.store.update({
      captured: true,
      listening: false,
      clarity,
      verdict: result.status,
      confidence: result.confidence,
      strings,
      issues,
      advice: stringAdvice(strings),
      hasIndeterminate: strings.some((s) => s.state === 'indeterminate'),
    });
  };
}
